"use client";

import DOMPurify from "isomorphic-dompurify";
import { Link } from "@/i18n/navigation";
import { useTranslations, useLocale } from "next-intl";
import type { BlogPost } from "@/lib/blog-posts";
import { formatDate } from "@/lib/blog-posts";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";

// CLIENT-005: sanitize blog content before rendering. Currently the posts are
// static, but the CMS migration (per CLAUDE.md roadmap) will flow operator-
// supplied HTML through this component. DOMPurify enforces an allowlist so
// even a compromised CMS entry cannot exfiltrate auth cookies or inject JS.
const ALLOWED_TAGS = [
  "p", "br", "h1", "h2", "h3", "h4", "h5", "h6",
  "strong", "em", "u", "s", "blockquote",
  "ul", "ol", "li",
  "a", "img",
  "hr", "code", "pre",
];
const ALLOWED_ATTR = ["href", "title", "target", "rel", "src", "alt", "width", "height"];

function sanitizeBlogContent(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "style"],
  });
}

export function BlogPostPage({
  post,
  relatedPosts,
}: {
  post: BlogPost;
  relatedPosts: BlogPost[];
}) {
  const t = useTranslations("blog");
  const tBlog = useTranslations("blogPage");
  const tCommon = useTranslations("common");
  const locale = useLocale();
  // CLIENT-005: sanitize once at render time. The resulting HTML contains
  // only allowlisted tags/attributes — no scripts, event handlers, or URL
  // schemes. Safe to hand to React's HTML sink.
  const safeBlogHtml = sanitizeBlogContent(post.content);
  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-8 lg:py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link
              href="/"
              className="hover:text-foreground transition-colors"
            >
              {tCommon("home")}
            </Link>
            <span>/</span>
            <Link
              href="/blog"
              className="hover:text-foreground transition-colors"
            >
              {tBlog("breadcrumb")}
            </Link>
            <span>/</span>
            <span className="text-text-secondary line-clamp-1">
              {post.title}
            </span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12 lg:gap-16">
            {/* Main content */}
            <article>
              {/* Meta */}
              <div className="flex items-center gap-3 mb-4">
                <span className="label-sm text-cta">{tBlog(post.category === "Tutorial" ? "categoryTutorial" : "categoryInformatie")}</span>
                <span className="text-xs text-text-tertiary">
                  {formatDate(post.date, locale)}
                </span>
              </div>

              <h1 className="heading-lg mb-4">{post.title}</h1>
              <p className="text-sm text-text-secondary mb-8">
                {t("by")} {post.author}
              </p>

              <div className="divider-bold mb-8" />

              {/* YouTube embed */}
              {post.youtubeId && (
                <div className="relative aspect-video mb-10 bg-surface border border-border overflow-hidden rounded-2xl">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${post.youtubeId}`}
                    title={post.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              )}

              {/* Article content — sanitized via DOMPurify allowlist (CLIENT-005) */}
              <div
                className="prose-unity"
                dangerouslySetInnerHTML={{ __html: safeBlogHtml }}
              />

              {/* CTA */}
              <div className="mt-12 pt-8 border-t border-border">
                <p className="text-sm text-text-body mb-6 leading-relaxed">
                  {t("ctaText")}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    href="/collecties/gezicht"
                    className="inline-flex items-center justify-center h-12 px-8 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors rounded-lg"
                    style={{
                      transitionDuration: "var(--duration-fast)",
                      transitionTimingFunction: "var(--ease-out)",
                    }}
                  >
                    {t("viewProducts")}
                  </Link>
                  <a
                    href="https://wa.me/31657222000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-12 px-8 border border-foreground/20 text-foreground label-sm hover:border-foreground/40 transition-colors rounded-lg"
                    style={{
                      transitionDuration: "var(--duration-fast)",
                      transitionTimingFunction: "var(--ease-out)",
                    }}
                  >
                    {t("personalAdvice")}
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="label-sm mb-4">{t("relatedArticles")}</h3>
                <nav className="space-y-4">
                  {relatedPosts.map((p) => (
                    <Link
                      key={p.slug}
                      href={`/blog/${p.slug}`}
                      className="group block"
                    >
                      <div className="flex gap-3">
                        <div className="w-16 h-16 shrink-0 overflow-hidden bg-surface border border-border rounded-xl">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={p.featuredImage}
                            alt={p.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <span className="text-[10px] uppercase tracking-wider text-cta font-medium">
                            {tBlog(p.category === "Tutorial" ? "categoryTutorial" : "categoryInformatie")}
                          </span>
                          <h4 className="text-sm text-text-secondary group-hover:text-foreground transition-colors line-clamp-2 leading-snug mt-0.5">
                            {p.title}
                          </h4>
                        </div>
                      </div>
                    </Link>
                  ))}
                </nav>

                <div className="mt-8 pt-6 border-t border-border">
                  <Link
                    href="/blog"
                    className="inline-flex items-center gap-1.5 label-sm text-text-secondary hover:text-foreground transition-colors"
                    style={{ transitionDuration: "var(--duration-fast)" }}
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    >
                      <path d="M19 12H5M11 18l-6-6 6-6" />
                    </svg>
                    {t("allArticles")}
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Back link (mobile) */}
          <div className="mt-8 lg:hidden">
            <Link
              href="/blog"
              className="inline-flex items-center gap-1.5 label-sm text-text-secondary hover:text-foreground transition-colors"
              style={{ transitionDuration: "var(--duration-fast)" }}
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M19 12H5M11 18l-6-6 6-6" />
              </svg>
              {t("backToBlog")}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
